import * as signalR from "@microsoft/signalr";
import { apiUrl } from "../server";

/**
 * Сервис для работы с SignalR уведомлениями об изменениях записей
 */
class RecordSignalRService {
  constructor() {
    this.connection = null;
    this.sessionId = null;
    this.currentDate = null;
    this.isConnected = false;
    this.eventHandlers = {
      onRecordAdded: null,
      onRecordUpdated: null,
      onRecordDeleted: null,
      onReconnected: null,
      onError: null
    };
  }

  /**
   * Инициализация и подключение к SignalR Hub
   */
  async initialize(sessionId, date, handlers = {}) {
    try {
      this.sessionId = sessionId;
      this.currentDate = date;
      this.eventHandlers = { ...this.eventHandlers, ...handlers };

      // Создаем подключение
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(`${apiUrl}/hubs/record`, {
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Error)
        //.configureLogging(signalR.LogLevel.Information)
        .build();

      // Настраиваем обработчики событий
      this.setupEventHandlers();

      // Подключаемся
      await this.connection.start();
      // console.log("SignalR подключен к RecordHub");
      this.isConnected = true;

      // Подписываемся на изменения записей
      const result = await this.subscribe(sessionId, date);
      // console.log("Подписка на записи:", result);

      return result;
    } catch (error) {
      console.error("Ошибка инициализации SignalR:", error);
      this.isConnected = false;
      if (this.eventHandlers.onError) {
        this.eventHandlers.onError(error);
      }
      throw error;
    }
  }

  /**
   * Настройка обработчиков событий SignalR
   */
  setupEventHandlers() {
    if (!this.connection) return;

    // Обработка добавления записи
    this.connection.on("RecordAdded", (event) => {
      // console.log("SignalR: Новая запись добавлена", event);
      if (this.eventHandlers.onRecordAdded) {
        this.eventHandlers.onRecordAdded(event);
      }
    });

    // Обработка изменения записи
    this.connection.on("RecordUpdated", (event) => {
      // console.log("SignalR: Запись изменена", event);
      if (this.eventHandlers.onRecordUpdated) {
        this.eventHandlers.onRecordUpdated(event);
      }
    });

    // Обработка удаления записи
    this.connection.on("RecordDeleted", (event) => {
      // console.log("SignalR: Запись удалена", event);
      if (this.eventHandlers.onRecordDeleted) {
        this.eventHandlers.onRecordDeleted(event);
      }
    });

    // Обработка переподключения
    this.connection.onreconnected(async (connectionId) => {
      console.log("SignalR переподключен:", connectionId);
      this.isConnected = true;
      
      // Переподписываемся на события
      if (this.sessionId && this.currentDate) {
        try {
          await this.subscribe(this.sessionId, this.currentDate);
          console.log("Переподписка выполнена успешно");
        } catch (error) {
          console.error("Ошибка переподписки:", error);
        }
      }

      if (this.eventHandlers.onReconnected) {
        this.eventHandlers.onReconnected();
      }
    });

    // Обработка разрыва соединения
    this.connection.onreconnecting((error) => {
      // console.warn("SignalR переподключение...", error);
      this.isConnected = false;
    });

    // Обработка закрытия соединения
    this.connection.onclose((error) => {
      // console.error("SignalR соединение закрыто", error);
      this.isConnected = false;
      if (this.eventHandlers.onError && error) {
        this.eventHandlers.onError(error);
      }
    });
  }

  /**
   * Подписка на изменения записей за определенную дату
   */
  async subscribe(sessionId, date) {
    if (!this.connection || !this.isConnected) {
      throw new Error("SignalR не подключен");
    }

    try {
      const dateString = typeof date === 'string' ? date : date.format('YYYY-MM-DD');
      const result = await this.connection.invoke(
        "SubscribeToRecordChanges",
        sessionId,
        dateString
      );
      
      this.sessionId = sessionId;
      this.currentDate = date;
      
      return result;
    } catch (error) {
      console.error("Ошибка подписки:", error);
      throw error;
    }
  }

  /**
   * Отписка от изменений записей
   */
  async unsubscribe() {
    if (!this.connection || !this.isConnected) {
      return;
    }

    try {
      const result = await this.connection.invoke("UnsubscribeFromRecordChanges");
      console.log("Отписка выполнена:", result);
      return result;
    } catch (error) {
      console.error("Ошибка отписки:", error);
      throw error;
    }
  }

  /**
   * Изменение даты подписки
   */
  async changeDate(newDate) {
    if (!this.connection || !this.isConnected) {
      throw new Error("SignalR не подключен");
    }

    try {
      const dateString = typeof newDate === 'string' ? newDate : newDate.format('YYYY-MM-DD');
      const result = await this.connection.invoke(
        "ChangeSubscribedDate",
        dateString
      );
      
      this.currentDate = newDate;
      console.log("Дата изменена:", result);
      
      return result;
    } catch (error) {
      console.error("Ошибка смены даты:", error);
      throw error;
    }
  }

  /**
   * Остановка и отключение от SignalR Hub
   */
  async stop() {
    if (!this.connection) {
      return;
    }

    try {
      await this.unsubscribe();
      await this.connection.stop();
      console.log("SignalR отключен");
      this.isConnected = false;
      this.connection = null;
    } catch (error) {
      console.error("Ошибка остановки SignalR:", error);
      throw error;
    }
  }

  /**
   * Проверка состояния подключения
   */
  getConnectionState() {
    if (!this.connection) {
      return "Disconnected";
    }
    return this.connection.state;
  }
}

// Экспортируем singleton instance
export default new RecordSignalRService();

